import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ZodError } from 'zod';
import { fetchy } from '@bridebook/web/lib/fetchy';
import { getScreenType } from '@bridebook/web/lib/app/selectors';
import { getCurrentUserId } from '@bridebook/web/lib/users/selectors';
import { markTodayTaskAsCompleted } from '@bridebook/web/lib/task/actions';
import { getWeddingId } from '@bridebook/web/lib/weddings/selectors';
import { useCachedSelector, useDispatch, useSelector } from '@bridebook/web/lib/utils';
import { ISupplierCalendarEventAnswer } from '@bridebook/models/source/models/Suppliers/Calendars/Events.types';
import { ApiError } from '@bridebook/toolbox/src/api/api-error';
import { getUserFriendlyErrorMessage } from '../utils';
import { useTranslation } from 'next-i18next';
import { ShowroundFormData, useShowroundFormSchema } from './use-showround-form-schema';
import { useShowroundBookingAnalytics } from './use-showround-booking-analytics';
import { getShowroundSupplierContextId } from '../state';
import { isSupplierShortlisted } from '@bridebook/web/lib/shortlist/selectors';
import { ARRANGE_VIEWING_TASK_DEFINITION } from '@bridebook/web/components/task/today-task/mobile/task-definitions';
import { resolveMobileAssignment } from '@bridebook/web/components/task/today-task/mobile/assignment';

export type CreateBookingAnswer = Pick<
  ISupplierCalendarEventAnswer,
  'questionId' | 'question' | 'answer'
>;

interface BookingState {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: ApiError | Error | null;
  userFriendlyError: string | null;
}

export function useShowroundBooking(calendarId: string, isOpenDay?: boolean) {
  const { t } = useTranslation('enquiries');
  const queryClient = useQueryClient();
  const supplierId = useSelector(getShowroundSupplierContextId);
  const isShortlisted = useCachedSelector(isSupplierShortlisted, supplierId);
  const userId = useSelector(getCurrentUserId);
  const weddingId = useSelector(getWeddingId);
  const dispatch = useDispatch();
  const { isMobile } = useSelector(getScreenType);
  const flowAssignment = useSelector(resolveMobileAssignment);
  const { showroundFormSchema } = useShowroundFormSchema(undefined, undefined, isOpenDay);
  const { trackEmailedSupplier, trackFailedToSendEnquiry, trackShortlistedSupplier } =
    useShowroundBookingAnalytics(isOpenDay);

  const [userFriendlyError, setUserFriendlyError] = useState<string | null>(null);

  const validateBookingData = useCallback(
    (formData: Partial<ShowroundFormData>) => {
      try {
        showroundFormSchema.parse(formData);
        return { isValid: true, errors: {} };
      } catch (error) {
        const zodErrors: Record<string, string> = {};
        if (error instanceof ZodError) {
          error.errors.forEach((err) => {
            const path = err.path.join('.');
            zodErrors[path] = err.message;
          });
        }
        return { isValid: false, errors: zodErrors };
      }
    },
    [showroundFormSchema],
  );

  const mutation = useMutation({
    mutationFn: async ({
      answers,
      startsAt,
      eventId,
    }: {
      answers?: CreateBookingAnswer[];
      startsAt?: string;
      eventId?: string;
    }) => {
      if (!userId || !weddingId || !supplierId) return;

      if (eventId) {
        return await fetchy().authenticated.post(
          '/api/supplier/[id]/calendar/[calendarId]/events/[eventId]/join',
          {
            id: supplierId,
            calendarId,
            eventId,
          },
          {
            weddingId,
            ...(answers && answers.length > 0 && { answers }),
          },
        );
      }

      if (!startsAt) throw new Error('startsAt is required to book a showround');

      return await fetchy().authenticated.post(
        '/api/supplier/[id]/calendar/[calendarId]/events/book',
        {
          id: supplierId,
          calendarId,
        },
        {
          weddingId,
          startsAt,
          ...(answers && answers.length > 0 && { answers }),
        },
      );
    },
    onSuccess: (_data, _variables) => {
      queryClient.invalidateQueries(['calendar-slots', supplierId, calendarId]);
      queryClient.invalidateQueries(['calendars', supplierId]);
      queryClient.invalidateQueries(['requested-showrounds', supplierId]);

      setUserFriendlyError(null);
    },
    onError: (error: ApiError | Error) => {
      const friendlyMessage = getUserFriendlyErrorMessage(error, t);
      setUserFriendlyError(friendlyMessage);
    },
  });

  const bookShowround = useCallback(
    async (
      formData: ShowroundFormData,
      questionAnswers: CreateBookingAnswer[] = [],
      eventId?: string,
    ) => {
      setUserFriendlyError(null);

      const validation = validateBookingData(formData);
      if (!validation.isValid) {
        const firstError = Object.values(validation.errors)[0];
        setUserFriendlyError(firstError);
        return { success: false, errors: validation.errors };
      }

      try {
        const result = await mutation.mutateAsync({
          startsAt: formData.selectedSlot,
          answers: questionAnswers,
          eventId,
        });
        trackEmailedSupplier(result?.eventId);
        !isShortlisted && trackShortlistedSupplier();
        if (isMobile && flowAssignment === 'venueNotSelected') {
          dispatch(markTodayTaskAsCompleted(ARRANGE_VIEWING_TASK_DEFINITION));
        }
        return { success: true, data: result };
      } catch (error) {
        trackFailedToSendEnquiry();
        return {
          success: false,
          error: error as ApiError | Error,
          errors: { general: userFriendlyError || 'Booking failed' },
        };
      }
    },
    [
      validateBookingData,
      mutation,
      trackEmailedSupplier,
      isShortlisted,
      trackShortlistedSupplier,
      trackFailedToSendEnquiry,
      userFriendlyError,
      isMobile,
      flowAssignment,
      dispatch,
    ],
  );

  const retry = useCallback(() => {
    mutation.reset();
    setUserFriendlyError(null);
  }, [mutation]);

  const bookingState: BookingState = {
    isLoading: mutation.isLoading,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error as ApiError | Error | null,
    userFriendlyError,
  };

  return {
    ...bookingState,
    bookShowround,
    validateBookingData,
    retry,
    canRetry: mutation.isError,
  };
}
